import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Row,
  Col,
  Divider,
  Select,
  Button,
  message,
  Typography,
  Input,
} from 'antd'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRightOutlined } from '@ant-design/icons'
import ForwardingPartsTable from './ForwardingPartsTable'
import OrderAddressView from '$components/OrderAddressView'
import FollowUpTreatmentsView from '$components/FollowUpTreatmentsView'
import ExpandedHeader from '$pages/serviceprovider/ReceivedRequests/CreateOffersView/ExpandedHeader'
import CreateOfferDetailView from '$pages/serviceprovider/ReceivedRequests/CreateOffersView/CreateOfferDetailView'
import SlideContentWrapper from '$components/SlideContentWrapper'
import { getServiceProviderCompanies, updateOrder } from '$api/evoAPIs'
import Form from '$components/Form'
import PropTypes from 'prop-types'

const { Text } = Typography

const ExpandedForwardingRow = ({ orderPartsList }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [orderPartsListForm] = Form.useForm()

  const [selectedProviderName, setSelectedProviderName] = useState(
    orderPartsList.service_provider_company || null,
  )
  const isForwarded = !!orderPartsList.service_provider_company
  const [operatorMaterialSelections, setOperatorMaterialSelections] = useState(
    () => {
      const initialSelections = {}
      orderPartsList.list_of_orderparts.forEach((orderPart) => {
        if (orderPart.operator_additive_material) {
          initialSelections[orderPart.id] =
            orderPart.operator_additive_material.id
        }
      })
      return initialSelections
    },
  )
  const [materialSelections, setMaterialSelections] = useState(() => {
    const initialSelections = {}
    orderPartsList.list_of_orderparts.forEach((orderPart) => {
      if (orderPart.service_provider_material) {
        initialSelections[orderPart.id] = orderPart.service_provider_material.id
      }
    })
    return initialSelections
  })

  const [showDetailView, setShowDetailView] = useState(false)
  const [isFirstOpen, setIsFirstOpen] = useState(true)
  const [currentStep, setCurrentStep] = useState(0)
  const [selectedPartKey, setSelectedPartKey] = useState(null)
  const [orderPartsFormValues, setOrderPartsFormValues] = useState({})

  const subOrders = [
    ...(orderPartsList.list_of_orderparts?.length
      ? orderPartsList.list_of_suborders
      : [orderPartsList.main_order]),
  ]

  const { data: companies, isLoading: loadingCompanies } = useQuery({
    queryFn: getServiceProviderCompanies,
  })

  const forwardMutation = useMutation({
    mutationFn: async () => {
      const operatorNote = orderPartsListForm.getFieldValue('operator_note')

      const orderParts = orderPartsList.list_of_orderparts.map((orderPart) => {
        const selectedMaterialId = materialSelections[orderPart.id]

        if (!selectedMaterialId)
          throw new Error(
            t(
              'operator_orders.select_all_materials_warning',
              'Please select a material for every part',
            ),
          )

        return {
          orderPartId: orderPart.id,
          operatorAdditiveMaterialId: operatorMaterialSelections[orderPart.id],
          serviceProviderMaterialId: selectedMaterialId,
        }
      })

      return updateOrder({
        orderPartListId: orderPartsList.id,
        serviceProvider: selectedProviderName,
        operatorNote,
        orderParts,
      })
    },
    onSuccess: () => {
      message.success(t('operator_orders.success_forward'))
      queryClient.invalidateQueries(['allorderedpartslists_operator'])
    },
    onError: (error) => {
      message.error(
        error.message ||
          t('operator_orders.error_forward', 'Failed to forward order'),
      )
    },
  })

  const handleForward = () => {
    if (!selectedProviderName) {
      message.warning(
        t(
          'operator_orders.select_provider_warning',
          'Please select a Service Provider',
        ),
      )
      return
    }
    const allPartsHaveMaterial = orderPartsList.list_of_orderparts.every(
      (part) => !!materialSelections[part.id],
    )
    if (!allPartsHaveMaterial) {
      message.warning(
        t(
          'operator_orders.select_all_materials_warning',
          'Please select a material for every part',
        ),
      )
      return
    }

    forwardMutation.mutate()
  }

  const next = (key) => {
    setShowDetailView(true)
    setIsFirstOpen(false)
    setCurrentStep(1)
    setSelectedPartKey(key)
  }
  const back = () => {
    setCurrentStep(0)
    setShowDetailView(false)
  }
  const deleteProviderOffer = () => {} // Mock
  const deleteRule = () => {} // Mock

  const nonDiscardedParts = orderPartsList.list_of_orderparts.filter(
    (p) => p.status_info !== 'discarded',
  )
  const forwardedPartsCount = nonDiscardedParts.filter(
    (p) => p.status_info === 'forwarded',
  ).length
  const progressPercentage =
    (forwardedPartsCount / (nonDiscardedParts.length || 1)) * 100

  const handleChangeServiceProviderName = (value) => {
    if (value !== selectedProviderName) {
      setSelectedProviderName(value)
      setMaterialSelections({})
    }
  }

  return (
    <>
      <ExpandedHeader offersCreatedPercentage={progressPercentage} />

      <div
        style={{
          maxWidth: '1240px',
          overflow: 'hidden',
          padding: '0 20px 20px 20px',
        }}
      >
        <SlideContentWrapper
          showDetailView={showDetailView}
          isFirstOpen={isFirstOpen}
          currentStep={currentStep}
          firstSlide={
            <>
              <div
                style={{
                  marginBottom: '20px',
                  padding: '15px',
                  backgroundColor: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: '4px',
                }}
              >
                <Text strong style={{ marginRight: '10px' }}>
                  {isForwarded
                    ? t('operator_orders.forwarded_to')
                    : t('operator_orders.select_service_provider_label')}
                </Text>
                <Select
                  style={{ width: '300px' }}
                  placeholder={t('operator_orders.select_provider')}
                  value={selectedProviderName}
                  disabled={isForwarded}
                  onChange={(value) => handleChangeServiceProviderName(value)}
                  loading={loadingCompanies}
                  options={companies?.map((c) => ({
                    label: c,
                    value: c,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </div>

              <h4 style={{ padding: '15px 0' }}>
                {t('received_requests.ordered_parts')}
              </h4>

              <ForwardingPartsTable
                orderParts={orderPartsList.list_of_orderparts}
                selectedProviderName={selectedProviderName}
                clientCompany={orderPartsList.client_company}
                materialSelections={materialSelections}
                setMaterialSelections={setMaterialSelections}
                operatorMaterialSelections={operatorMaterialSelections}
                setOperatorMaterialSelections={setOperatorMaterialSelections}
                isForwarded={isForwarded}
                providerName={
                  companies?.find((c) => c.id === selectedProviderName)?.name
                }
                next={next}
                currentStep={currentStep}
                initialMaterials={orderPartsList.list_of_orderparts.reduce(
                  (acc, part) => {
                    if (part.service_provider_material) {
                      acc[part.id] = part.service_provider_material.id
                    }
                    return acc
                  },
                  {},
                )}
              />

              <Form form={orderPartsListForm} layout="vertical">
                <div style={{ marginTop: '20px' }}>
                  <Row gutter={24}>
                    <Col span={16}>
                      <OrderAddressView
                        billingAddress={orderPartsList.billing_address}
                        deliveryAddress={orderPartsList.delivery_address}
                      />
                    </Col>
                  </Row>

                  <Divider style={{ margin: '12px 0' }} />
                  <Row gutter={24}>
                    <Col span={12}>
                      <h4>
                        {t('received_requests.follow_up_treatments.title')}
                      </h4>
                      <div
                        style={{
                          background: '#fff',
                          border: '1px solid #f0f0f0',
                          borderRadius: '4px',
                          padding: '15px',
                        }}
                      >
                        {orderPartsList.list_of_orderparts.map((part) => (
                          <div key={part.id} style={{ marginBottom: '15px' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'baseline',
                                marginBottom: '5px',
                              }}
                            >
                              <Text strong style={{ marginRight: '10px' }}>
                                {part.part.id_part_client}
                              </Text>
                              <Text
                                type="secondary"
                                style={{ fontSize: '12px' }}
                              >
                                {part.part.name}
                              </Text>
                            </div>
                            <div
                              style={{
                                paddingLeft: '10px',
                                borderLeft: '2px solid #1890ff',
                              }}
                            >
                              <FollowUpTreatmentsView orderPart={part} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </Col>
                    <Col span={12}>
                      {orderPartsList?.client_note && (
                        <>
                          <h4>{t('service.order_part.additional_notes')}</h4>
                          <div
                            style={{
                              background: '#f5f5f5',
                              padding: '10px',
                              borderRadius: '4px',
                              minHeight: '60px',
                              marginBottom: '15px',
                            }}
                          >
                            {orderPartsList.client_note}
                          </div>
                        </>
                      )}
                      <Form.Item
                        label={t('operator_orders.operator_note')}
                        name="operator_note"
                        initialValue={orderPartsList?.operator_note}
                      >
                        <Input.TextArea
                          rows={4}
                          placeholder={
                            isForwarded
                              ? '—'
                              : t('operator_orders.add_operator_note')
                          }
                          disabled={isForwarded}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Form>

              <Divider />

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '15px 0',
                }}
              >
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={handleForward}
                  loading={forwardMutation.isLoading}
                  size="large"
                  disabled={!selectedProviderName || isForwarded}
                >
                  {isForwarded
                    ? t('operator_orders.order_forwarded')
                    : t('operator_orders.transfer')}
                </Button>
              </div>
            </>
          }
          secondSlide={
            selectedPartKey && (
              <CreateOfferDetailView
                key={selectedPartKey}
                back={back}
                orderPartsListId={orderPartsList.id}
                orderPart={orderPartsList.list_of_orderparts.find(
                  (p) => p.id === selectedPartKey,
                )}
                orderPartId={selectedPartKey}
                currentStep={currentStep}
                subOrders={subOrders}
                setOrderPartsFormValues={setOrderPartsFormValues}
                orderPartsFormValues={orderPartsFormValues}
                deleteRule={deleteRule}
                deleteProviderOffer={deleteProviderOffer}
              />
            )
          }
        />
      </div>
    </>
  )
}

ExpandedForwardingRow.propTypes = {
  orderPartsList: PropTypes.object,
}

export default ExpandedForwardingRow
